import type {
  PlinthMaterialId,
  PlinthMaterialSpec,
} from './types';

/**
 * Vanilla starter set. Six cross-compatible materials that look good under
 * any of the five skybox presets. All PBR-param only — no textures, no effects —
 * so they cost nothing extra to ship.
 *
 * When introducing premium materials later, add them here with `tier:
 * 'premium'` and the picker will gate them automatically.
 */
export const PLINTH_MATERIALS: readonly PlinthMaterialSpec[] = [
  {
    id: 'stone',
    name: 'Stone',
    sublabel: 'Speckled grey granite',
    tier: 'vanilla',
    top: '#5b5b5b',
    body: '#4a4a4a',
    socle: '#2f2f2f',
    trim: '#8a8a8a',
    roughness: 0.88,
    metalness: 0.08,
    envMapIntensity: 0.22,
    capTopEmissiveIntensity: 0.5,
    capSideEmissiveIntensity: 0.07,
  },
  {
    id: 'obsidian',
    name: 'Obsidian',
    sublabel: 'Cool glassy black',
    tier: 'vanilla',
    top: '#16161c',
    body: '#0a0a0e',
    socle: '#050507',
    // Cool steel edge — keeps today's aurora/sci-fi plinth accents alive.
    trim: '#8aa4c0',
    // Glassy: sharp reflections, moderate roughness map variation.
    roughness: 0.12,
    metalness: 0.35,
    envMapIntensity: 0.82,
    capTopEmissiveIntensity: 0.4,
    capSideEmissiveIntensity: 0.06,
    capEmissiveTint: '#b8c8dd',
  },
  {
    id: 'marble',
    name: 'Marble',
    sublabel: 'Bright veined polish',
    tier: 'vanilla',
    top: '#e5e1d6',
    body: '#c8c4b9',
    socle: '#7f7b70',
    // Warm bronze trim — matches today's `sky` plinth feel.
    trim: '#e2c58c',
    // Polished stone: strong environment reflections, barely metallic.
    roughness: 0.13,
    metalness: 0.02,
    envMapIntensity: 1.45,
    capTopEmissiveIntensity: 0.8,
    capSideEmissiveIntensity: 0.26,
    capEmissiveTint: '#f7f4ee',
  },
  {
    id: 'sandstone',
    name: 'Sandstone',
    sublabel: 'Warm desert grain',
    tier: 'vanilla',
    top: '#d4ae82',
    body: '#b8946a',
    socle: '#7a6040',
    trim: '#c79a4a',
    roughness: 0.94,
    metalness: 0,
    envMapIntensity: 0.1,
    capTopEmissiveIntensity: 0.58,
    capSideEmissiveIntensity: 0.12,
    capEmissiveTint: '#f2dcc0',
  },
  {
    id: 'slate',
    name: 'Slate',
    sublabel: 'Blue-grey layers',
    tier: 'vanilla',
    top: '#4a5560',
    body: '#3a454a',
    socle: '#222830',
    trim: '#9fb8c8',
    roughness: 0.68,
    metalness: 0.12,
    envMapIntensity: 0.42,
    capTopEmissiveIntensity: 0.52,
    capSideEmissiveIntensity: 0.1,
    capEmissiveTint: '#d0dbe8',
  },
  {
    id: 'wood',
    name: 'Wood',
    sublabel: 'Walnut oiled grain',
    tier: 'vanilla',
    // Warm walnut with honeyed highlights — reads as a real playing board.
    top: '#7a4a28',
    body: '#5a3519',
    socle: '#2e1a0c',
    trim: '#caa064',
    // Fully diffuse — no HDRI reflections; visibility comes from warm emissive fill.
    roughness: 0.94,
    metalness: 0,
    envMapIntensity: 0,
    capTopEmissiveIntensity: 0.74,
    capSideEmissiveIntensity: 0.2,
    capEmissiveTint: '#e8c9a4',
  },
  // ---------------------------------------------------------------------
  // Premium 1-of-1: Boardroom velvet (regal emerald drape with gold trim).
  // The painter renders draped folds + faint gold filaments; the Plinth
  // component additionally hangs four gold tassels at the corners when
  // it sees `id === 'velvet'`. Marked `tier: 'premium'` so pickers
  // filter it out of community board / player surfaces.
  // ---------------------------------------------------------------------
  {
    id: 'velvet',
    name: 'Velvet',
    sublabel: 'Regal emerald · cabal cloth',
    tier: 'premium',
    // Deep emerald palette: top is the lit folds catching key light,
    // body is the shadowed drape, socle is where the cloth pools at the
    // floor. All very saturated greens — black-base velvet would
    // disappear against the Sanctum sky.
    top: '#13573b',
    body: '#0a3a26',
    socle: '#04200f',
    // Warm antique gold rim — the Plinth's metallic trim slot makes this
    // read as braided gold cord between body and slab.
    trim: '#d4a04a',
    // Fabric: high roughness floor, no metalness, very low env so the
    // cloth never picks up sky reflections (would shatter the velvet
    // illusion). Brightness comes from the painted fold-shading + a
    // gentle emissive warm tint that simulates internal velvet glow.
    roughness: 0.92,
    metalness: 0,
    envMapIntensity: 0.05,
    capTopEmissiveIntensity: 0.55,
    capSideEmissiveIntensity: 0.18,
    capEmissiveTint: '#1a8a5a',
  },
];

export const PLINTH_MATERIAL_BY_ID: Record<PlinthMaterialId, PlinthMaterialSpec> =
  PLINTH_MATERIALS.reduce(
    (acc, m) => ({ ...acc, [m.id]: m }),
    {} as Record<PlinthMaterialId, PlinthMaterialSpec>,
  );
